
import { Component, Prop, Vue } from "vue-property-decorator";
import Booking from "@/domain/booking/Booking";
import Room from "@/domain/restaurant/Room";
import Table from "@/domain/restaurant/Table";
import ConfirmationDialog from "@/components/alert/ConfirmationDialog.vue";
import RemoteServices from "@/services/RemoteServices";
import WorkingHours from "@/domain/restaurant/WorkingHours";
import Restaurant from "@/domain/restaurant/Restaurant";

export interface manualOverrideItem {
  original: Booking;
  changed: Booking;
  selectedRoom: Room | null;
}

@Component({
  components: { ConfirmationDialog }
})
export default class BookingManualOverrideComponent extends Vue {
  @Prop({ default: () => [] })
  bookings!: manualOverrideItem[];

  @Prop({ default: () => {} })
  roomDict!: { [key: number]: Room };

  @Prop({ default: () => {} })
  tableDict!: { [key: number]: Table };

  @Prop({ default: null })
  workingHours!: WorkingHours;

  @Prop({ default: null })
  selectedRestaurant!: Restaurant;

  showRoomSelect: boolean = false;

  loading = false;

  headers = [
    {
      text: this.$t("booking.time"),
      value: "original.bookingDateTime",
      groupable: false,
      width: 10
    },
    {
      text: this.$t("booking.numberOfPeople"),
      value: "original.numberOfPeople",
      groupable: false,
      width: 100
    },
    {
      text: this.$t("booking.customerName"),
      value: "original.customerName",
      groupable: false,
      width: 50
    },
    {
      text: this.$t("booking.status.label"),
      value: "original.status",
      groupable: false,
      width: 50
    },
    {
      text: this.$t("booking.manualOverride.selectedRoom"),
      value: "selectedRoom",
      groupable: false,
      sortable: true,
      width: 100
    },
    {
      text: this.$t("booking.manualOverride.original.table"),
      value: "original.tableId",
      groupable: false,
      sortable: true,
      width: 100
    },
    {
      text: this.$t("booking.manualOverride.changed.table"),
      value: "changed.tableId",
      groupable: false,
      sortable: false,
      width: 100
    }
  ];

  get allTablesSelected() {
    for (let i = 0; i < this.bookings.length; i++)
      if (this.bookings[i].changed.tableId == null) return false;
    return true;
  }

  getRoomColourFromTableId(tableId: number): string {
    if (this.tableDict[tableId] != null)
      return Room.getRoomColourFromId(this.tableDict[tableId].roomId);
    return "red";
  }

  getTableNameFromId(tableId: number) {
    if (this.tableDict[tableId] != null)
      return this.tableDict[tableId].tableName;
    return "";
  }

  get changedBookings(): Booking[] {
    const toReturn: Booking[] = [];
    for (let i = 0; i < this.bookings.length; i++)
      toReturn.push(this.bookings[i].changed);
    return toReturn;
  }

  get confirmationDialog() {
    return this.$refs.confirmationDialog as ConfirmationDialog;
  }

  get possibleRooms() {
    const toReturn = [];
    for (const key in this.roomDict) {
      toReturn.push(this.roomDict[key]);
    }
    return toReturn;
  }

  originalTable(booking: Booking): Table | null {
    if (booking.tableId !== undefined) return this.tableDict[booking.tableId];
    return new Table();
  }

  async confirmSubmission() {
    if (
      await this.confirmationDialog.open(
        this.$tc("booking.manualOverride.title"),
        this.$tc("booking.manualOverride.confirmation.message")
      )
    ) {
      this.loading = true;
      await this.submitManualOverride();
      this.loading = false;
    }
  }

  async submitManualOverride() {
    try {
      const bookings = await RemoteServices.doManualOverride(
        this.workingHours,
        this.changedBookings
      );
      await this.$store.dispatch(
        "success",
        this.$t("booking.manualOverride.success")
      );
      this.emitManualOverride(bookings);
    } catch (error) {
      this.$store.dispatch("error", error.message);
    }
  }

  onRoomChange(item: manualOverrideItem) {
    item.changed.tableId = undefined;
  }

  emitManualOverride(bookings: Booking[]) {
    this.$emit("manual-override", bookings);
  }

  getBookingTableAssignmentIcon(booking: Booking) {
    if (this.selectedRestaurant != null)
      return booking.getTableAssignmentIcon(this.selectedRestaurant, true).icon;
    return "";
  }

  getBookingTableAssignmentIconColour(booking: Booking) {
    if (this.selectedRestaurant != null)
      return booking.getTableAssignmentIcon(this.selectedRestaurant, true)
        .colour;
    return "";
  }

  getBookingStatusIcon(booking: Booking) {
    if (this.selectedRestaurant != null) {
      return booking.getBookingStatusIcon().icon;
    }
    return "";
  }

  getBookingStatusIconColour(booking: Booking) {
    if (this.selectedRestaurant != null)
      return booking.getBookingStatusIcon().colour;
    return "";
  }
}
